import React, { useEffect, useMemo, useState } from "react";
import { FileText, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { TRY, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const VAT_RATES = [0, 1, 5, 8, 10, 18, 20];
const COST_CATS = { GIDA: "Gıda", PERSONEL: "Personel", LOJISTIK: "Lojistik", AMBALAJ: "Ambalaj", GENEL: "Genel", EKSTRA: "Ekstra Maliyet" };
const todayISO = () => new Date().toISOString().slice(0, 10);
const emptyLine = (vat) => ({ name: "", qty: "", unit_price: "", vat_rate: vat });

export default function InvoiceDialog({ open, onClose, kind, parties, defaultPartyId, onSaved, editItem }) {
  const isSale = kind === "sale";
  const [form, setForm] = useState({});
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editItem) {
      setForm({ party_id: editItem.customer_id || editItem.supplier_id, invoice_no: editItem.invoice_no || "", date: editItem.date, due_date: editItem.due_date || "",
        description: "", cost_category: editItem.cost_category || "GIDA" });
      setLines((editItem.lines || []).map((l) => ({ name: l.name, qty: String(l.qty), unit_price: String(l.unit_price), vat_rate: l.vat_rate })));
      return;
    }
    const pid = defaultPartyId || parties[0]?.id || "";
    const p = parties.find((x) => x.id === pid);
    const vat = isSale ? (p?.vat_rate ?? 10) : 10;
    setForm({ party_id: pid, invoice_no: "", date: todayISO(), due_date: "", description: "", cost_category: p?.category || "GIDA" });
    setLines([emptyLine(vat)]);
    // eslint-disable-next-line
  }, [open, kind, editItem]);

  const pick = (pid) => {
    const p = parties.find((x) => x.id === pid);
    setForm((f) => ({ ...f, party_id: pid, cost_category: p?.category || f.cost_category }));
    if (isSale && p) setLines((ls) => ls.map((l) => ({ ...l, vat_rate: p.vat_rate ?? l.vat_rate })));
  };
  const setLine = (i, patch) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const addLine = () => lines.length < 6 && setLines((ls) => [...ls, emptyLine(ls[ls.length - 1]?.vat_rate ?? 10)]);
  const removeLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, j) => j !== i) : ls));

  const totals = useMemo(() => {
    let net = 0, vat = 0, qty = 0;
    for (const l of lines) {
      const q = parseFloat(l.qty) || 0, p = parseFloat(l.unit_price) || 0;
      const n = q * p; net += n; vat += n * (Number(l.vat_rate) || 0) / 100; qty += q;
    }
    return { net, vat, total: net + vat, qty };
  }, [lines]);

  const save = async () => {
    const valid = lines.filter((l) => l.name.trim() && parseFloat(l.qty) > 0);
    if (!form.party_id || valid.length === 0) { toast.error(isSale ? "Müşteri ve en az bir kalem gerekli" : "Tedarikçi ve en az bir kalem gerekli"); return; }
    setSaving(true);
    try {
      const base = isSale ? "/ledger/invoice" : "/supplier-ledger/invoice";
      const payload = {
        ...form, due_date: form.due_date || undefined,
        lines: valid.map((l) => ({ name: l.name.trim(), qty: parseFloat(l.qty), unit_price: parseFloat(l.unit_price) || 0, vat_rate: Number(l.vat_rate) || 0 })),
      };
      if (editItem) await api.put(`${base}/${editItem.id}`, payload); else await api.post(base, payload);
      toast.success(editItem ? "Fatura güncellendi" : isSale ? "Satış faturası cariye işlendi" : "Alış faturası kaydedildi ve maliyete işlendi");
      onClose(); onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><FileText className={`h-5 w-5 ${isSale ? "text-primary" : "text-rose-400"}`} /> {isSale ? "Satış Faturası" : "Alış Faturası"}{editItem && <span className="text-sm font-normal text-muted-foreground">— Düzenle</span>}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <F label={isSale ? "Müşteri" : "Tedarikçi"} className="col-span-2">
              <Select value={form.party_id || ""} onValueChange={pick}>
                <SelectTrigger data-testid="invoice-party"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>{parties.map((p) => <SelectItem key={p.id} value={p.id}>{isSale ? p.name : (p.short_name || p.name)}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Fatura No"><Input value={form.invoice_no || ""} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} data-testid="invoice-no" className="font-mono" placeholder="FT-2026-001" /></F>
            <F label="Fatura Tarihi"><Input type="date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="invoice-date" className="font-mono" /></F>
            <F label={isSale ? "Vade Tarihi (boş = müşteri vadesi)" : "Vade Tarihi (boş = tedarikçi vadesi)"} className="col-span-2"><Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="invoice-due" className="font-mono" /></F>
            {!isSale && (
              <F label="Maliyet Kategorisi" className="col-span-2">
                <Select value={form.cost_category || "GIDA"} onValueChange={(v) => setForm({ ...form, cost_category: v })}>
                  <SelectTrigger data-testid="invoice-cost-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(COST_CATS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </F>
            )}
            {isSale && <F label="Açıklama" className="col-span-2"><Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="invoice-description" placeholder="Opsiyonel not" /></F>}
          </div>

          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm" data-testid="invoice-lines">
              <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr><th className="px-2 py-2 text-left w-8">#</th><th className="px-2 py-2 text-left">Nevi (Mal / Hizmet)</th><th className="px-2 py-2 text-right w-24">Adet</th><th className="px-2 py-2 text-right w-32">Birim Fiyat</th><th className="px-2 py-2 text-right w-24">KDV %</th><th className="px-2 py-2 text-right w-32">Tutar</th><th className="w-10" /></tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const n = (parseFloat(l.qty) || 0) * (parseFloat(l.unit_price) || 0);
                  return (
                    <tr key={i} className="border-t border-border/60" data-testid={`invoice-line-${i}`}>
                      <td className="px-2 py-1.5 text-muted-foreground font-mono">{i + 1}</td>
                      <td className="px-2 py-1.5"><Input value={l.name} onChange={(e) => setLine(i, { name: e.target.value })} data-testid={`invoice-line-name-${i}`} className="h-9" placeholder={isSale ? "Örn: Öğle yemeği" : "Örn: Tavuk but"} /></td>
                      <td className="px-2 py-1.5"><Input type="number" min={0} value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} data-testid={`invoice-line-qty-${i}`} className="h-9 text-right font-mono no-spin" placeholder="0" /></td>
                      <td className="px-2 py-1.5"><Input type="number" min={0} step="0.01" value={l.unit_price} onChange={(e) => setLine(i, { unit_price: e.target.value })} data-testid={`invoice-line-price-${i}`} className="h-9 text-right font-mono no-spin" placeholder="0,00" /></td>
                      <td className="px-2 py-1.5">
                        <Select value={String(l.vat_rate)} onValueChange={(v) => setLine(i, { vat_rate: Number(v) })}>
                          <SelectTrigger data-testid={`invoice-line-vat-${i}`} className="h-9 font-mono"><SelectValue /></SelectTrigger>
                          <SelectContent>{VAT_RATES.map((r) => <SelectItem key={r} value={String(r)}>%{r}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono" data-testid={`invoice-line-total-${i}`}>{TRY(n * (1 + (Number(l.vat_rate) || 0) / 100))}</td>
                      <td className="px-1 py-1.5 text-right">
                        <button type="button" onClick={() => removeLine(i)} disabled={lines.length === 1} className="p-1.5 rounded hover:bg-accent text-muted-foreground disabled:opacity-30" data-testid={`invoice-line-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex items-center justify-between px-3 py-2 border-t border-border/60 bg-muted/20">
              <Button variant="ghost" size="sm" onClick={addLine} disabled={lines.length >= 6} data-testid="invoice-add-line" className="gap-1.5 h-8"><Plus className="h-3.5 w-3.5" /> Kalem Ekle ({lines.length}/6)</Button>
              <div className="text-xs text-muted-foreground">Toplam adet: <span className="font-mono text-foreground">{totals.qty.toLocaleString("tr-TR")}</span></div>
            </div>
          </div>

          <div className="ml-auto w-full sm:w-72 space-y-1.5 text-sm" data-testid="invoice-totals">
            <Row l="Ara Toplam" v={TRY(totals.net)} />
            <Row l="KDV Toplamı" v={TRY(totals.vat)} />
            <div className="border-t border-border pt-2 flex justify-between font-semibold text-base"><span>Genel Toplam</span><span className="font-mono text-primary" data-testid="invoice-grand-total">{TRY(totals.total)}</span></div>
          </div>
          {!isSale && <p className="text-xs text-muted-foreground">Genel toplam seçilen kategoriye maliyet olarak otomatik eklenir.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={save} disabled={saving} data-testid="invoice-save" className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} {editItem ? "Değişiklikleri Kaydet" : "Faturayı Kaydet"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const F = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}><Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>
);
const Row = ({ l, v }) => <div className="flex justify-between"><span className="text-muted-foreground">{l}</span><span className="font-mono">{v}</span></div>;
